import './AiModalCard.scss';

import { AiModalCardFooter } from './AiModalCardFooter';
import { Button } from 'antd';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SalesforceLogo } from '@ui/icons';
import { useOnMount } from '@hooks';

import { AiMarkdownContent, SonarCard, SparkleIcon } from '@ui/general';
import { createGetStreamEndpoint, ErrorOrFailStatusCode, HttpHeader } from '@sonar-software/sonar-http';
import React, { useRef, useState } from 'react';

export interface AiModalCardProps {
	allowDownload?: boolean;
	onClose?: () => void;
	salesforceUrl?: string;
	showIcon?: boolean;
	sourceUrl: string,
	title: string;
}

export const AiModalCard: React.FC<AiModalCardProps> = ({
	allowDownload,
	onClose,
	salesforceUrl,
	showIcon,
	sourceUrl,
	title
}) => {
	const [ text, setText ] = useState('');
	const [ hasLoaded, setHasLoaded ] = useState(false);
	const [ hasError, setHasError ] = useState(false);
	const [ hasPromptLengthError, setHasPromptLengthError ] = useState(false);
	const [ aiRequestLogId, setAiRequestLogId ] = useState('');
	const cardRef = useRef<HTMLDivElement>(null);

	useOnMount(() => {
		if (!sourceUrl) {
			setHasError(true);
		}

		const load = createGetStreamEndpoint(sourceUrl);

		let reader: ReadableStreamDefaultReader | undefined = undefined;

		load().then(async(response) => {
			try {
				reader = response.data;

				if (!reader) {
					setHasError(true);
					setHasLoaded(true);

					if (response.status === ErrorOrFailStatusCode.BadRequest) {
						setHasPromptLengthError(true);
					}

					return;
				}

				const id = response.headers.get(HttpHeader.AssociatedId);

				if (id) {
					setAiRequestLogId(id);
				}

				const decoder = new TextDecoder();

				while (true) {
				// eslint-disable-next-line no-await-in-loop
					const { done, value } = await reader.read();

					if (done) {
						break;
					}

					const stringValue = decoder.decode(value, { stream: true });

					setText((current) => current + stringValue);
				}

				setHasLoaded(true);
			} catch {
				setHasError(true);
				setHasLoaded(true);
			} finally {
				reader?.releaseLock();
			}
		});

		return () => {
			reader?.releaseLock();
		};
	});

	const displayError = hasError || (hasLoaded && !text);

	return (
		<SonarCard
			className='ai-modal-card'
			extra={ salesforceUrl ?
				<Button
					className='open-in-salesforce-button'
					href={ salesforceUrl }
					icon={ <SalesforceLogo color='currentColor' /> }
					target='_blank'
					type='default'>
					{ COPY_SPECIFIC.OPEN_IN_SALESFORCE }
				</Button> : undefined }
			footer={ aiRequestLogId ?
				<AiModalCardFooter
					aiRequestLogId={ aiRequestLogId }
					allowDownload={ allowDownload }
					cardRef={ cardRef }
					key={ aiRequestLogId }
					onCancel={ onClose } /> : undefined }
			icon={ showIcon ? <SparkleIcon /> : undefined }
			ref={ cardRef }
			title={ title }>
			<AiMarkdownContent
				hasError={ displayError }
				hasPromptLengthError={ hasPromptLengthError }
				text={ text } />
		</SonarCard>
	);
};
